import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import styled from "styled-components";

// Imported assets
import {
  CopyFileIcon,
  FilterIcon,
  SearchIcon,
} from "../../../assets/icons/svgs/Icons";

// Imported components
import ConfiguratorModal from "./components/ConfiguratorModal";
import NavigationBar from "../../../components/nav/NavigationBar";
import PrimaryButton from "../../../components/ui/PrimaryButton";
import ViewPolicyModal from "./components/ViewPolicyModal";

// Styled components
const ContentModeButton = styled(PrimaryButton)`
  background: ${({ $isSelected }) =>
    $isSelected ? "var(--terbine-blue)" : "#D9D9D9"};
  color: ${({ $isSelected }) => ($isSelected ? "white" : "black")};
  font-size: 12px;
  height: 40px;
  padding: 0 20px;
  width: auto;

  &:hover {
    background: lab(
      from
        ${({ $isSelected }) =>
          $isSelected ? "var(--terbine-blue)" : "#D9D9D9"}
        calc(l - 5) a b / 1
    );
  }
`;

const AllPoliciesButton = styled(ContentModeButton)`
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
`;

const CurrentPoliciesButton = styled(ContentModeButton)`
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
`;

const Container = styled.div`
  align-items: stretch;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: clamp(60px, 5vw, 80px) clamp(50px, 5vw, 100px) 0;
  position: relative;
  width: 100vw;
`;

const ContentControls = styled.div`
  // display: flex;
  // flex-direction: row;
  // justify-content: space-between;
  // margin: 10px 0;

  & .MuiGrid-item {
    padding-left: 0 !important;
    padding-top: 0 !important;
  }
`;

const ContentModeButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
  align-content: space-around;
`;

const FiltersButton = styled(PrimaryButton)`
  display: flex;
  flex-direction: row;
  gap: 12px;
  height: 40px;
  width: 150px;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 1);
  }
`;

const policiesTestData = [
  {
    policyName: "Name 1",
    description: "Description 1",
    attributes: [
      {
        type: "attr",
        attrType: "Equipment Type",
        boolModifier: "IS",
        attrValue: "ChargerID",
      },
      { type: "cond", conditional: "AND" },
      {
        type: "attr",
        attrType: "City",
        boolModifier: "IS NOT",
        attrValue: "Boston",
      },
    ],
    designatedRoutingType: "Public",
    routingValue: "",
    startDate: "2025-05-01",
    endDate: "2025-12-31",
    isAnonymized: true,
    anonymizerReplacerText: "Replacer text 1",
    enabled: true,
    notes: "Note 1",
  },
  {
    policyName: "Name 2",
    description: "Description 2",
    attributes: [
      {
        type: "attr",
        attrType: "Country",
        boolModifier: "IS",
        attrValue: "United States",
      },
      { type: "cond", conditional: "AND" },
      {
        type: "attr",
        attrType: "Standard Type",
        boolModifier: "IS",
        attrValue: "OCPP",
      },
      { type: "cond", conditional: "AND" },
      {
        type: "attr",
        attrType: "Equipment Type",
        boolModifier: "IS",
        attrValue: "ChargerID",
      },
      { type: "cond", conditional: "AND" },
      {
        type: "attr",
        attrType: "Accessibility",
        boolModifier: "IS",
        attrValue: "Public",
      },
    ],
    designatedRoutingType: "API Access Code",
    routingValue: "",
    startDate: "2025-05-01",
    endDate: "2025-12-31",
    isAnonymized: false,
    anonymizerReplacerText: "",
    enabled: true,
    notes: "Note 2",
  },
  {
    policyName: "Name 3",
    description: "Description 3",
    attributes: [
      {
        type: "attr",
        attrType: "County",
        boolModifier: "IS",
        attrValue: "Los Angeles County",
      },
      { type: "cond", conditional: "AND" },
      {
        type: "attr",
        attrType: "Equipment Type",
        boolModifier: "IS",
        attrValue: "StationID",
      },
      { type: "cond", conditional: "AND" },
      {
        type: "attr",
        attrType: "Owner",
        boolModifier: "IS",
        attrValue: "Los Angeles Fire Dept.",
      },
    ],
    designatedRoutingType: "Domain Members",
    routingValue: "@domain.com",
    startDate: "2025-05-01",
    endDate: "2025-12-31",
    isAnonymized: true,
    anonymizerReplacerText: "Replacer text 3",
    enabled: true,
    notes: "Note 3",
  },
];

const Policies = () => {
  const [contentMode, setContentMode] = useState("All"); // "Current" or "All"

  const [search, setSearch] = useState("");

  return (
    <Container>
      <NavigationBar />

      <Typography
        align="center"
        gutterBottom
        variant="h4"
        sx={{
          fontFamily: "Oxygen, Inter, Roboto, sans-serif",
          padding: "clamp(30px, 5vw, 60px) 0 0 0",
        }}
      >
        Policies
      </Typography>

      <ConfiguratorModal />

      <ContentControls>
        <Grid
          container
          direction="row"
          columnGap={2}
          sx={{ margin: 0, padding: "10px 0", width: "100%" }}
          wrap="nowrap"
        >
          <Grid item xs={3} sm={true} sx={{ pl: 0, pt: 0 }}>
            <FormControl fullWidth>
              <TextField
                onChange={(e) => {
                  // setTablePage(0);
                  setSearch(e.target.value);
                }}
                placeholder="Search"
                size="small"
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon />,
                    // endAdornment: search ? (
                    //   <Icon
                    //     onClick={() => setSearch("")}
                    //     src={CancelIcon}
                    //     style={{
                    //       cursor: "pointer",
                    //       marginLeft: "10px",
                    //       marginRight: "0",
                    //     }}
                    //   />
                    // ) : (
                    //   <Icon style={{ marginLeft: "10px", marginRight: "0" }} />
                    // ),
                  },
                }}
                value={search}
              />
            </FormControl>
          </Grid>

          <Grid item xs={"auto"} sm={2.5} sx={{ pl: 0, pt: 0 }}>
            <ContentModeButtons>
              <CurrentPoliciesButton
                $isSelected={contentMode === "Current"}
                onClick={() => setContentMode("Current")}
              >
                Show Current Policies
              </CurrentPoliciesButton>
              <AllPoliciesButton
                $isSelected={contentMode === "All"}
                onClick={() => setContentMode("All")}
              >
                Show All Policies
              </AllPoliciesButton>
            </ContentModeButtons>
          </Grid>

          <Grid item xs={3} sm={1} sx={{ pl: 0, pt: 0 }}>
            <FiltersButton>
              Filter
              <FilterIcon fill="white" />
            </FiltersButton>
          </Grid>
        </Grid>
      </ContentControls>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          "& *": {
            fontFamily: "Inter, Oxygen, Roboto, sans-serif",
          },
        }}
      >
        <Table
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "var(--terbine-blue)",
                "& th": {
                  color: "white",
                  fontSize: "clamp(12px, 1.5vw, 18px)",
                  fontWeight: 300,
                  borderBottom: "none",
                },
                borderBottom: "none",
              }}
            >
              <TableCell width="4%" />
              {[
                { label: "Name" },
                { label: "Selected Attributes", width: "16%" },
                { label: "Criteria", width: "16%" },
                { label: "Routing", width: "13%" },
                { align: "center", label: "Time Period", width: "10%" },
                { align: "center", label: "Anonymous?", width: "10%" },
              ].map((header, i) => (
                <TableCell
                  align={header.align || "left"}
                  key={i}
                  width={header.width || null}
                >
                  {header.label}
                </TableCell>
              ))}
              <TableCell width="6%" />
              <TableCell width="4%" />
              <TableCell width="4%" />
            </TableRow>
          </TableHead>
          <TableBody>
            {policiesTestData.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  backgroundColor: "#F2F2F2",
                  borderBottom:
                    index !== policiesTestData.length - 1
                      ? "3px solid white"
                      : "none",
                  borderTop: "none",
                  "& > td": {
                    borderTop: "none",
                    borderBottom:
                      index !== policiesTestData.length - 1
                        ? "3px solid white"
                        : "none",
                    color: "black",
                  },
                }}
              >
                <TableCell>
                  <Checkbox />
                </TableCell>
                <TableCell>{row.policyName}</TableCell>
                <TableCell>
                  {row.attributes.reduce(
                    (acc, curr) =>
                      curr.attrType === undefined
                        ? acc
                        : acc === ""
                        ? curr.attrType
                        : acc + ", " + curr.attrType,
                    ""
                  )}
                </TableCell>
                <TableCell>
                  {row.attributes.reduce(
                    (acc, curr) =>
                      curr.attrValue === undefined
                        ? acc
                        : acc === ""
                        ? curr.attrValue
                        : acc + ", " + curr.attrValue,
                    ""
                  )}
                </TableCell>
                <TableCell>{row.designatedRoutingType}</TableCell>
                <TableCell align="center">
                  {`${row.startDate} -\n${row.endDate}`}
                </TableCell>
                <TableCell align="center">
                  <Checkbox checked={row.isAnonymized} />
                </TableCell>
                <TableCell>
                  <Switch checked={row.enabled} />
                </TableCell>
                <TableCell>
                  <ViewPolicyModal data={row} />
                </TableCell>
                <TableCell>
                  <div
                    style={{
                      display: "flex",
                      cursor: "pointer",
                      justifyContent: "center",
                    }}
                  >
                    <CopyFileIcon mr="0" />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Policies;
